import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { useDarkMode } from "../../contexts/DarkModeContext";
import styles from './SmartAssistant.module.css';

const SmartAssistant = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "مرحباً! أنا مساعد علمني الذكي، كيف يمكنني مساعدتك اليوم؟",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // استخدام DarkModeContext الموحد
  const { isDarkMode } = useDarkMode();
  
  // استخدام متغير البيئة لـ Google API key
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  
  // إنشاء مثيل Google Generative AI
  let genAI = null;
  if (apiKey) {
    try {
      genAI = new GoogleGenerativeAI(apiKey);
    } catch (error) {
    }
  }
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    // التحقق من وجود API key
    if (!apiKey) {
      const errorMessage = {
        id: Date.now() + 1,
        text: 'عذراً، مفتاح API غير متوفر. يرجى إنشاء ملف .env مع REACT_APP_GOOGLE_API_KEY لتفعيل مساعد علمني',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    // التحقق من وجود مثيل AI
    if (!genAI) {
      const errorMessage = {
        id: Date.now() + 1,
        text: 'عذراً، لم يتم تهيئة المساعد الذكي. يرجى التحقق من إعدادات API.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // استخدام Google Gemini 2.5 Flash (أحدث وأسرع نموذج)
      let model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });
      
      const prompt = `أنت مساعد ذكي ومفيد تتحدث باللغة العربية. قدم إجابات مفيدة ومختصرة ومفصلة حسب الحاجة. 

سؤال المستخدم: ${inputValue}

أجب باللغة العربية:`;

      let result;
      let attempts = 0;
      const maxAttempts = 3;
      
      while (attempts < maxAttempts) {
        try {
          result = await model.generateContent(prompt);
          break; // نجح، اخرج من الحلقة
        } catch (attemptError) {
          attempts++;
          console.log(`محاولة ${attempts} فشلت:`, attemptError.message);
          
          if (attempts >= maxAttempts) {
            throw attemptError; // استسلم بعد كل المحاولات
          }
          
          // إذا كان النموذج مزدحماً، انتظر قليلاً ثم حاول مرة أخرى
          if (attemptError.message.includes('overloaded') || attemptError.message.includes('503')) {
            // أضف رسالة للمستخدم
            const retryMessage = {
              id: Date.now() + 0.1,
              text: `⏳ النموذج مزدحم حالياً، جاري المحاولة مرة أخرى... (محاولة ${attempts}/${maxAttempts})`,
              isUser: false,
              timestamp: new Date(),
              isRetry: true
            };
            setMessages(prev => [...prev, retryMessage]);
            
            console.log('النموذج مزدحم، انتظار 2 ثانية قبل إعادة المحاولة...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // جرب النموذج الاحتياطي في المحاولة الثانية
            if (attempts === 2) {
              console.log('جاري تجربة النموذج الاحتياطي gemini-1.5-flash...');
              model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });
            }
          } else {
            throw attemptError; // خطأ آخر، لا تحاول مرة أخرى
          }
        }
      }
      
      const response = await result.response;
      
      const text = response.text();
      
      if (text) {
        // إزالة رسائل إعادة المحاولة
        setMessages(prev => prev.filter(msg => !msg.isRetry));
        
        const assistantMessage = {
          id: Date.now() + 1,
          text: text,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error('لم يتم الحصول على رد من المساعد - النص فارغ');
      }
    } catch (error) {
      console.error('Error:', error);
      
      // إزالة رسائل إعادة المحاولة
      setMessages(prev => prev.filter(msg => !msg.isRetry));
      
      let errorMessage = 'عذراً، حدث خطأ في الاتصال. يرجى المحاولة مرة أخرى.';
      
      // رسائل خطأ أكثر تفصيلاً لـ Google API
      if (error.message.includes('overloaded')) {
        errorMessage = 'النموذج مزدحم حالياً. يرجى المحاولة مرة أخرى بعد قليل.';
      } else if (error.message.includes('400')) {
        errorMessage = 'خطأ في الطلب. يرجى التحقق من صحة الرسالة.';
      } else if (error.message.includes('401')) {
        errorMessage = 'خطأ في مفتاح API. يرجى التحقق من صحة المفتاح.';
      } else if (error.message.includes('403')) {
        errorMessage = 'تم رفض الطلب. يرجى التحقق من صلاحيات API.';
      } else if (error.message.includes('429')) {
        errorMessage = 'تم تجاوز حد الطلبات المسموح. يرجى الانتظار قليلاً قبل المحاولة مرة أخرى.';
      } else if (error.message.includes('500')) {
        errorMessage = 'خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.';
      } else if (error.message.includes('503')) {
        errorMessage = 'النموذج مزدحم حالياً. يرجى المحاولة مرة أخرى بعد قليل.';
      } else if (error.message.includes('API key')) {
        errorMessage = 'مفتاح API غير صحيح أو منتهي الصلاحية.';
      } else if (error.message.includes('quota')) {
        errorMessage = 'تم استنفاذ الحصة المخصصة لـ API.';
      } else if (error.message.includes('model')) {
        errorMessage = 'النموذج المطلوب غير متوفر أو غير مدعوم.';
      } else if (error.message.includes('content')) {
        errorMessage = 'المحتوى المرسل غير مقبول أو يحتوي على محتوى ضار.';
      }
      
      // إضافة تفاصيل الخطأ للرسالة
      const detailedError = `${errorMessage}\n\nتفاصيل الخطأ: ${error.message}`;
      
      const errorMsg = {
        id: Date.now() + 1,
        text: detailedError,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('ar-EG', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // دالة لحفظ المحادثة في localStorage
  const saveConversation = () => {
    try {
      localStorage.setItem('smartAssistant_conversation', JSON.stringify(messages));
      alert('تم حفظ المحادثة بنجاح!');
    } catch (error) {
      alert('حدث خطأ في حفظ المحادثة');
    }
  };

  // دالة لمسح المحادثة
  const clearConversation = () => {
    if (window.confirm('هل أنت متأكد من رغبتك في مسح المحادثة؟')) {
      setMessages([
        {
          id: Date.now(),
          text: "مرحباً! أنا مساعد علمني الذكي، كيف يمكنني مساعدتك اليوم؟",
          isUser: false,
          timestamp: new Date()
        }
      ]);
    }
  };

  return (
    <div className={`smart-assistant-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={{padding: '140px 0'}}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}>🤖</div>
            <h1>مساعدك الذكي</h1>
          </div>
          <p className={styles.subtitle}>مساعد ذكي من علمني يساعدك في حل مشاكلك وإجابة أسئلتك</p>
          
          {/* أزرار التحكم */}
          <div className={styles.headerActions}>
            <button onClick={clearConversation} className={styles.actionButton}>
              🗑️ مسح المحادثة
            </button>
          </div>
        </div>
      </div>

      <div className={styles.chatContainer}>
        {/* رسالة تحذير إذا لم يكن هناك API key */}
        {!apiKey && (
          <div className={styles.warningMessage}>
            <div className={styles.warningIcon}>⚠️</div>
            <div className={styles.warningText}>
              <strong>تحذير:</strong> مفتاح API غير موجود
              <br />
              يرجى إنشاء ملف <code>.env</code> في المجلد الجذر مع المحتوى التالي:
              <br />
              <code>REACT_APP_GOOGLE_API_KEY=your_google_api_key_here</code>
              <br />
              ثم إعادة تشغيل الخادم
            </div>
          </div>
        )}

        <div className={styles.messagesContainer}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`${styles.message} ${message.isUser ? styles.userMessage : styles.assistantMessage}`}
            >
              <div className={styles.messageContent}>
                <div className={styles.messageText}>{message.text}</div>
                <div className={styles.messageTime}>
                  {formatTime(message.timestamp)}
                </div>
              </div>
              <div className={styles.messageAvatar}>
                {message.isUser ? '👤' : '🤖'}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className={`${styles.message} ${styles.assistantMessage}`}>
              <div className={styles.messageContent}>
                <div className={styles.typingIndicator}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className={styles.messageAvatar}>🤖</div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
                             placeholder={!apiKey ? "مفتاح API غير متوفر لتفعيل مساعد علمني" : "اكتب رسالتك هنا..."}
              className={styles.input}
              rows="1"
              disabled={isLoading || !apiKey}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading || !apiKey}
              className={styles.sendButton}
            >
              {isLoading ? (
                <div className={styles.spinner}></div>
              ) : (
                <span>إرسال</span>
              )}
            </button>
          </div>
          <div className={styles.inputHint}>
            {!apiKey ? "قم بإعداد مفتاح API أولاً" : "اضغط Enter للإرسال، Shift + Enter للسطر الجديد"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartAssistant;